import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";

export default function CollectionDatesChart({ data = [] }) {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

    const { dates, values, yMax, isolates } = useMemo(() => {
        const safe = Array.isArray(data) ? data : [];

        const dates = safe.map((d) => d?.date ?? "");
        const values = safe.map((d) => Number(d?.value ?? 0));

        const isolates = safe.reduce((acc, item) => {
            return acc + (Number(item?.value) || 0);
        }, 0);

        const maxValue = values.length ? Math.max(...values) : 0;
        const yMax = Math.max(6, maxValue + 1);

        return { dates, values, yMax, isolates };
    }, [data]);

    const option = useMemo(
        () => ({
            grid: {
                left: isSmallScreen ? 10 : 50,
                right: isSmallScreen ? 10 : 30,
                top: 50,
                bottom: 45,
                containLabel: true,
            },

            tooltip: {
                trigger: "axis",
                axisPointer: {
                    type: "line",
                    lineStyle: { color: "#1A1A1A", width: 1 },
                },
                show: values.length > 0,
            },

            xAxis: {
                type: "category",
                data: dates,
                axisLabel: {
                    fontSize: isSmallScreen ? 10 : 12,
                    color: "#333",
                    interval: 0,
                },
                axisLine: { lineStyle: { color: "#333" } },
                axisTick: { lineStyle: { color: "#333" } },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: "#222",
                        type: "dashed",
                    },
                },
            },

            yAxis: {
                type: "value",
                min: 0,
                max: yMax,
                axisLabel: { fontSize: isSmallScreen ? 10 : 12, color: "#333" },
                axisLine: { lineStyle: { color: "#333" } },
                axisTick: { lineStyle: { color: "#333" } },
                splitLine: { show: false },
            },

            series: [
                {
                    id: "collectionDates",
                    type: "line",
                    data: values,
                    smooth: false,
                    symbol: "circle",
                    symbolSize: 6,
                    itemStyle: { color: "#000" },
                    lineStyle: { width: 3, color: "#20BC71" },
                    emphasis: {
                        itemStyle: {
                            borderColor: "#000",
                            borderWidth: 1,
                            color: "#000",
                        },
                    },
                },
            ],
        }),
        [dates, values, yMax, isSmallScreen]
    );

    return (
        <Box sx={{ width: "100%" }}>
            <Box
                sx={{
                    width: "100%",
                    height: { xs: 320, md: 420, lg: 480 },
                    background: "#fff",
                    border: "1px solid #222",
                    borderRadius: "40px",
                    position: "relative",
                    px: { xs: 1.5, md: 3 },
                    py: { xs: 2, md: 3 },
                    mb: 4,
                    overflow: "hidden",
                }}
            >
                <ReactECharts
                    option={option}
                    style={{ width: "100%", height: "100%" }}
                    notMerge={true}
                    lazyUpdate={true}
                />
            </Box>

            <Box sx={{ width: "100%", display: "flex", justifyContent: "end" }}>
                <Box
                    sx={{
                        width: "270px",
                        borderTop: "1px solid #222",
                        borderBottom: "1px solid #222",
                        textAlign: "end",
                        py: "10px",
                    }}
                >
                    <Typography variant="subText">{isolates} isolates total</Typography>
                </Box>
            </Box>
        </Box>
    );
}